interface Planning {
  disciplineId: string;
  discipline: string;
  planMinutes: number;
  therapyType: string;
  actualMinutes: number;
  isMissedVisit: boolean;
  hasSplit: any;
}

interface Plan {
  planId: number;
  date: string;
  totalPlanMinutes: number;
  totalActualMinutes: number;
  planning: Planning[];
  isConcurrentAllowed?: boolean;
  isGroupAllowed?: boolean;
}

export interface EditabilityFlags {
  isConcurrentAllowed: boolean;
  isGroupAllowed: boolean;
}

interface TherapyEntry {
  uuid: string;
  therapyType: string;
  date: string;
  planMinutes: number;
  actualMinutes: number;
  isMissedVisit: boolean;
  hasSplit: any;
}

interface TherapyMap {
  [key: string]: TherapyEntry[];
}

interface Result {
  otMap: TherapyMap;
  ptMap: TherapyMap;
  slpMap: TherapyMap;
  editabilityByDate: Record<string, EditabilityFlags>;
}

function generateDummyData(date: string): Plan {
  return {
    planId: 0, // Dummy ID, you can use a better strategy for unique IDs if needed
    date: date,
    totalPlanMinutes: 0,
    totalActualMinutes: 0,
    planning: [
      {
        disciplineId: '',
        discipline: 'OT',
        planMinutes: 0,
        therapyType: 'INDIVIDUAL',
        actualMinutes: 0,
        isMissedVisit: false,
        hasSplit: null,
      },
      {
        disciplineId: '',
        discipline: 'OT',
        planMinutes: 0,
        therapyType: 'CONCURRENT',
        actualMinutes: 0,
        isMissedVisit: false,
        hasSplit: null,
      },
      {
        disciplineId: '',
        discipline: 'OT',
        planMinutes: 0,
        therapyType: 'GROUP',
        actualMinutes: 0,
        isMissedVisit: false,
        hasSplit: null,
      },
      {
        disciplineId: '',
        discipline: 'PT',
        planMinutes: 0,
        therapyType: 'INDIVIDUAL',
        actualMinutes: 0,
        isMissedVisit: false,
        hasSplit: null,
      },
      {
        disciplineId: '',
        discipline: 'PT',
        planMinutes: 0,
        therapyType: 'CONCURRENT',
        actualMinutes: 0,
        isMissedVisit: false,
        hasSplit: null,
      },
      {
        disciplineId: '',
        discipline: 'PT',
        planMinutes: 0,
        therapyType: 'GROUP',
        actualMinutes: 0,
        isMissedVisit: false,
        hasSplit: null,
      },
      {
        disciplineId: '',
        discipline: 'SLP',
        planMinutes: 0,
        therapyType: 'INDIVIDUAL',
        actualMinutes: 0,
        isMissedVisit: false,
        hasSplit: null,
      },
      {
        disciplineId: '',
        discipline: 'SLP',
        planMinutes: 0,
        therapyType: 'CONCURRENT',
        actualMinutes: 0,
        isMissedVisit: false,
        hasSplit: null,
      },
      {
        disciplineId: '',
        discipline: 'SLP',
        planMinutes: 0,
        therapyType: 'GROUP',
        actualMinutes: 0,
        isMissedVisit: false,
        hasSplit: null,
      },
    ],
  };
}

// function getDatesInRange(startDate: string, endDate: string): string[] {
//   const dates: string[] = [];
//   const start = new Date(startDate);
//   const end = new Date(endDate);

//   for (let d = start; d <= end; d.setDate(d.getDate() + 1)) {
//     dates.push(d.toISOString().split('T')[0]); // Format: YYYY-MM-DD
//   }

//   return dates;
// }

// export function therapyPlaningResponseMapper(
//   data: Plan[],
//   startDate: string,
//   endDate: string
// ): Result {
//   const result: Result = {
//     otMap: {},
//     ptMap: {},
//     slpMap: {},
//   };

//   const dateRange = getDatesInRange(startDate, endDate);
//   const existingDates = new Set(data.map((plan) => plan.date));

//   // Check for missing dates and add dummy data if necessary
//   for (const date of dateRange) {
//     if (!existingDates.has(date)) {
//       data.push(generateDummyData(date));
//     }
//   }

//   // Process the data to fill therapy maps
//   for (const plan of data) {
//     for (const entry of plan.planning) {
//       const therapyEntry: TherapyEntry = {
//         uuid: entry.disciplineId,
//         therapyType: entry.therapyType,
//         date: plan.date,
//         planMinutes: entry.planMinutes,
//         actualMinutes: entry.actualMinutes,
//         isMissedVisit: false,
//       };

//       // Determine the correct map based on discipline
//       switch (entry.discipline) {
//         case 'OT':
//           if (!result.otMap[entry.therapyType]) {
//             result.otMap[entry.therapyType] = [];
//           }
//           result.otMap[entry.therapyType].push(therapyEntry);
//           break;
//         case 'PT':
//           if (!result.ptMap[entry.therapyType]) {
//             result.ptMap[entry.therapyType] = [];
//           }
//           result.ptMap[entry.therapyType].push(therapyEntry);
//           break;
//         case 'SLP':
//           if (!result.slpMap[entry.therapyType]) {
//             result.slpMap[entry.therapyType] = [];
//           }
//           result.slpMap[entry.therapyType].push(therapyEntry);
//           break;
//       }
//     }
//   }

//   if (data.length === 0) {
//     for (const date of dateRange) {
//       data.push(generateDummyData(date));
//     }
//   }

//   return result;
// }

//with sorted data
function getDatesInRange(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  const start = new Date(startDate);
  const end = new Date(endDate);

  for (let d = new Date(start.getTime()); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    const formattedDate = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
      .toISOString()
      .split('T')[0]; // Format: YYYY-MM-DD
    dates.push(formattedDate);
  }

  return dates;
}

function sortTherapyEntriesByDate(entries: TherapyEntry[]): TherapyEntry[] {
  return entries.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

export function therapyPlaningResponseMapper(
  data: Plan[],
  startDate: string,
  endDate: string
): Result {
  const result: Result = {
    otMap: {},
    ptMap: {},
    slpMap: {},
    editabilityByDate: {},
  };

  const dateRange = getDatesInRange(startDate, endDate);
  const existingDates = new Set(data.map((plan) => plan.date));

  // Check for missing dates and add dummy data if necessary
  for (const date of dateRange) {
    if (!existingDates.has(date)) {
      data.push(generateDummyData(date));
    }
  }

  // Backend returns a row per date (even empty ones) so it can carry per-day
  // isConcurrentAllowed / isGroupAllowed flags. Fill empty planning arrays
  // with dummy entries so the grid still renders 9 cells per date. Also pad
  // any per-date planning that's missing a (discipline, therapyType) combo
  // — e.g. after Anticipated Discharge is set, BE stops returning that
  // discipline's rows for post-discharge dates, which would leave the row
  // one cell short and misalign it with the header.
  for (const plan of data) {
    if (!plan.planning || plan.planning.length === 0) {
      plan.planning = generateDummyData(plan.date).planning;
      continue;
    }
    const dummyPlanning = generateDummyData(plan.date).planning;
    for (const dummyEntry of dummyPlanning) {
      const alreadyPresent = plan.planning.some(
        (e) => e.discipline === dummyEntry.discipline && e.therapyType === dummyEntry.therapyType
      );
      if (!alreadyPresent) plan.planning.push(dummyEntry);
    }
  }

  // Process the data to fill therapy maps
  for (const plan of data) {
    result.editabilityByDate[plan.date] = {
      isConcurrentAllowed: plan.isConcurrentAllowed ?? true,
      isGroupAllowed: plan.isGroupAllowed ?? true,
    };
    for (const entry of plan.planning) {
      const therapyEntry: TherapyEntry = {
        uuid: entry.disciplineId,
        therapyType: entry.therapyType,
        date: plan.date,
        planMinutes: entry.planMinutes,
        actualMinutes: entry.actualMinutes,
        isMissedVisit: entry.isMissedVisit,
        hasSplit: entry?.hasSplit,
      };

      // Skip if a cell for the same disciplineId + date already exists
      // (BE returns one planning row per associated taskUuid; we render one cell per disciplineId)
      const isDuplicateCell = (arr: TherapyEntry[] = []) =>
        arr.some((e) => e.uuid === therapyEntry.uuid && e.date === therapyEntry.date);

      // Determine the correct map based on discipline
      switch (entry.discipline) {
        case 'OT':
          if (!result.otMap[entry.therapyType]) {
            result.otMap[entry.therapyType] = [];
          }
          if (!isDuplicateCell(result.otMap[entry.therapyType])) {
            result.otMap[entry.therapyType].push(therapyEntry);
          }
          break;
        case 'PT':
          if (!result.ptMap[entry.therapyType]) {
            result.ptMap[entry.therapyType] = [];
          }
          if (!isDuplicateCell(result.ptMap[entry.therapyType])) {
            result.ptMap[entry.therapyType].push(therapyEntry);
          }
          break;
        case 'SLP':
          if (!result.slpMap[entry.therapyType]) {
            result.slpMap[entry.therapyType] = [];
          }
          if (!isDuplicateCell(result.slpMap[entry.therapyType])) {
            result.slpMap[entry.therapyType].push(therapyEntry);
          }
          break;
      }
    }
  }

  // Sort therapy maps by date
  for (const therapyType in result.otMap) {
    result.otMap[therapyType] = sortTherapyEntriesByDate(result.otMap[therapyType]);
  }

  for (const therapyType in result.ptMap) {
    result.ptMap[therapyType] = sortTherapyEntriesByDate(result.ptMap[therapyType]);
  }

  for (const therapyType in result.slpMap) {
    result.slpMap[therapyType] = sortTherapyEntriesByDate(result.slpMap[therapyType]);
  }

  // Handle case where data is empty
  if (data.length === 0) {
    for (const date of dateRange) {
      data.push(generateDummyData(date));
    }
  }

  return result;
}

//summary date missing

type TherapySummary = {
  date: string;
  summary: number;
};

type TherapyData = Record<string, TherapySummary[]>;

export const addMissingDates = (
  startDate: string,
  endDate: string,
  data: TherapyData
): TherapyData => {
  // Helper function to get all dates between start and end date
  const getDateRange = (start: string, end: string): string[] => {
    const dates: string[] = [];
    let current = new Date(start);
    const endDate = new Date(end);

    while (current <= endDate) {
      const formattedDate = new Date(
        Date.UTC(current.getUTCFullYear(), current.getUTCMonth(), current.getUTCDate())
      )
        .toISOString()
        .split('T')[0]; // Format YYYY-MM-DD
      dates.push(formattedDate);
      current.setUTCDate(current.getUTCDate() + 1);
    }

    return dates;
  };

  const { freqAlertOT, freqAlertPT, freqAlertSLP, isRequiredPlanning, ...restData } = data;

  const completeData: TherapyData = {};
  const dateRange = getDateRange(startDate, endDate);

  Object.entries(restData).forEach(([key, summaries]) => {
    const existingDates = new Set(summaries?.map((item) => item.date));

    // Add missing dates
    const filledSummaries = dateRange.map((date) => {
      if (existingDates.has(date)) {
        return summaries.find((item) => item.date === date)!; // Use existing data
      }
      return { date, summary: 0 }; // Add dummy data
    });

    completeData[key] = filledSummaries;
  });

  return completeData;
};

//total time and variation
interface TherapyPlan {
  date: string;
  totalPlanMinutes: number;
  totalActualMinutes: number;
}

interface ResponseData {
  date: string;
  totalPlanMinutes: number;
  totalActualMinutes: number;
}

// Function to generate dummy data for a given date
const generateDummyDataForVAriation = (date: string): ResponseData => ({
  date,
  totalPlanMinutes: 0,
  totalActualMinutes: 0,
});

// Function to fill missing dates with dummy data
export const fillMissingDates = (
  data: TherapyPlan[],
  startDate: string,
  endDate: string
): TherapyPlan[] => {
  const filledData: TherapyPlan[] = [];
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Create a Set of existing dates for quick lookup
  const existingDates = new Set(data.map((plan) => plan.date));

  // Iterate through each date in the range using UTC methods to avoid timezone issues
  for (let date = new Date(start.getTime()); date <= end; date.setUTCDate(date.getUTCDate() + 1)) {
    const dateString = new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
    )
      .toISOString()
      .split('T')[0]; // Format date as YYYY-MM-DD

    if (existingDates.has(dateString)) {
      // If the date exists, push the existing data
      const existingPlan = data.find((plan) => plan.date === dateString);
      if (existingPlan) {
        filledData.push(existingPlan);
      }
    } else {
      // If the date does not exist, add dummy data
      filledData.push(generateDummyDataForVAriation(dateString));
    }
  }

  return filledData;
};

interface ResponseDataOfLookBack {
  date: string;
  lookbackSum: number;
}

const generateDummyDataForLookBack = (date: string): ResponseDataOfLookBack => ({
  date,
  lookbackSum: 0,
});

// Function to fill missing dates with dummy data
export const fillMissingDatesForDateLookBack = (
  data: any,
  startDate: string,
  endDate: string
): any => {
  const filledData: any = [];
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Create a Set of existing dates for quick lookup
  const existingDates = new Set(data.map((plan: any) => plan.date));

  // Iterate through each date in the range using UTC methods to avoid timezone issues
  for (let date = new Date(start.getTime()); date <= end; date.setUTCDate(date.getUTCDate() + 1)) {
    const dateString = new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
    )
      .toISOString()
      .split('T')[0]; // Format date as YYYY-MM-DD

    if (existingDates.has(dateString)) {
      // If the date exists, push the existing data
      const existingPlan = data.find((plan: any) => plan.date === dateString);
      if (existingPlan) {
        filledData.push(existingPlan);
      }
    } else {
      // If the date does not exist, add dummy data
      filledData.push(generateDummyDataForLookBack(dateString));
    }
  }

  return filledData;
};
